/**
 * O IndexedDB é um banco de dados local do navegador.
 * 
 * Este arquivo representa uma biblioteca auxiliar que serve para manipular e manter o upload de arquivos temporários no browser até que ele seja, de fato, persistido no backend.
 * Os dados armazenados por ele persistem mesmo que o browser seja apagado. Mas ele é apagado quando se apaga o cache do browser.
 * 
 * O IndexedDB possui as seguintes vantagens:
 * 1- Dados ficam isolados por domínio
 * 2- Não são acessíveis por outros sites
 * 3- Podem ser apagados programaticamente
 * 4- 
 * 
 */

// IndexedDB com segurança em 5 camadas
// Camadas:
// 1) Session ID por aba
// 2) Dados indexados por sessão
// 3) Expiração automática (TTL)
// 4) Limpeza na inicialização
// 5) Limpeza best-effort no unload

const DB_NAME = 'secure-files-db';
const STORE_NAME = 'files';
const DB_VERSION = 1;
const TTL_MS = 5 * 60 * 1000; // 5 minutos

function getSessionId() {
  let sessionId = sessionStorage.getItem('SESSION_ID');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('SESSION_ID', sessionId);
  }
  return sessionId;
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('sessionId', 'sessionId', { unique: false });
        store.createIndex('expiresAt', 'expiresAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Remove todos os arquivos da sessão atual
export async function clearSessionFiles() {
  const db = await openDatabase();
  const sessionId = getSessionId();

  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('sessionId');

  return new Promise((resolve) => {
    const request = index.getAllKeys(sessionId);

    request.onsuccess = async () => {
      for (const key of request.result) {
        store.delete(key);
      }
      resolve();
    };

    request.onerror = () => resolve();
  });
}

// Limpeza por TTL
export async function cleanupExpired() {
  const db = await openDatabase();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const now = Date.now();

  return new Promise((resolve) => {
    const cursorRequest = store.openCursor();

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (cursor) {
        if (cursor.value.expiresAt < now) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };
  });
}

// Salvar arquivo
export async function saveFile(fileBase64, name, size, type) {
  await clearSessionFiles(); //Garante que exista apenas um único arquivo armazenado por aba no browser.
  const db = await openDatabase();
  const sessionId = getSessionId();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  store.put({
    id: `${sessionId}-${name}`,
    sessionId,
    name: name,
    size: size,
    type: type,
    data: fileBase64,
    expiresAt: Date.now() + TTL_MS,
  });

  return tx.complete;
}

// Ler arquivo da sessão atual
export async function readFile() {
  const db = await openDatabase();
  const sessionId = getSessionId();

  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const index = store.index('sessionId');

  return new Promise((resolve) => {
    const request = index.getAll(sessionId);

    request.onsuccess = () => {
      resolve(request.result.length ? request.result[0] : null);
    };

    request.onerror = () => resolve(null);
  });
}
